/**
 * DrePdfContext (Task #186) — ponte entre a página DRE e o botão "Relatório
 * PDF" do TopBar.
 *
 * O TopBar fica no AppShell, ancestral da FinanceiroDrePage, então não enxerga
 * o estado (regime/mês) nem o dado já buscado pela página. Em vez de prop-drill
 * por AppShell ou re-fetch no TopBar (buscaria DRE em toda tela), a página
 * PUBLICA a resposta v2 que já renderiza; o TopBar CONSOME pra montar o PDF.
 *
 * Resultado: PDF reflete exatamente a tela (mesmo regime/mês/dado), sem
 * fetch duplicado. Quando a página DRE não está montada (ou o dado ainda
 * carrega), o payload é null e o TopBar desabilita o botão.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { DreV2Response, DreRegime } from "../api/types";

export interface DrePdfPayload {
  data: DreV2Response;
  regime: DreRegime;
  mesRef: string;
}

interface DrePdfContextValue {
  payload: DrePdfPayload | null;
  publish: (payload: DrePdfPayload | null) => void;
}

const DrePdfContext = createContext<DrePdfContextValue | null>(null);

export function DrePdfProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<DrePdfPayload | null>(null);
  return (
    <DrePdfContext.Provider value={{ payload, publish: setPayload }}>
      {children}
    </DrePdfContext.Provider>
  );
}

/** Consumidor (TopBar): payload corrente pro PDF, ou null se indisponível. */
export function useDrePdfPayload(): DrePdfPayload | null {
  const ctx = useContext(DrePdfContext);
  return ctx?.payload ?? null;
}

/** Publicador (FinanceiroDrePage): mantém o payload sincronizado com o dado
 * em tela e limpa ao desmontar. `payload` null (loading/erro) também é
 * publicado pra desabilitar o botão. No-op fora do Provider (defensivo). */
export function useDrePdfPublisher(payload: DrePdfPayload | null): void {
  const ctx = useContext(DrePdfContext);
  const publish = ctx?.publish;
  useEffect(() => {
    if (!publish) return;
    publish(payload);
    return () => publish(null);
  }, [publish, payload]);
}
